import { apiClient } from '@/lib/api.ts'

export interface NinjaStatus {
  configured: boolean
  connected: boolean
  connectionError: string | null
  tokenCached: boolean
  tokenValidForMs: number | null
  tokenExpiresAt: number | null
}

export interface NinjaSyncResult {
  success: boolean
  action: 'create' | 'update'
  ninjaOrgId: number
  orgName: string
}

export interface NinjaSyncLogEntry {
  id: string
  customerId: string
  action: string
  ninjaoneOrgId: string | null
  triggeredBy: string
  status: string
  errorMessage: string | null
  createdAt: string
}

export interface NinjaPolicy {
  id: number
  name: string
  description?: string | null
  nodeClass?: string | null
}

export interface NinjaRole {
  id: number
  name: string
  nodeClass?: string | null
}

export interface OrgPolicyMapping {
  nodeRoleId: number
  policyId: number
}

export function getNinjaStatus() {
  return apiClient.get<NinjaStatus>('/ninja/status')
}

export function getNinjaConfig() {
  return apiClient.get<{ configured: boolean }>('/ninja/config')
}

export function saveNinjaConfig(clientId: string, clientSecret: string) {
  return apiClient.post<{ success: boolean }>('/ninja/config', { clientId, clientSecret })
}

export function refreshNinjaToken() {
  return apiClient.post<{ success: boolean }>('/ninja/token/refresh', {})
}

export function getNinjaAutoSync() {
  return apiClient.get<{ enabled: boolean }>('/ninja/auto-sync')
}

export function setNinjaAutoSync(enabled: boolean) {
  return apiClient.put<{ success: boolean; enabled: boolean }>('/ninja/auto-sync', { enabled })
}

export function syncCustomerToNinja(customerId: string) {
  return apiClient.post<NinjaSyncResult>(`/ninja/customers/${customerId}/sync`, {})
}

export function getNinjaSyncLog(customerId: string, limit = 20) {
  return apiClient.get<NinjaSyncLogEntry[]>(`/ninja/customers/${customerId}/sync-log?limit=${limit}`)
}

export function getNinjaPolicies() {
  return apiClient.get<NinjaPolicy[]>('/ninja/policies')
}

export function refreshNinjaPoliciesCache() {
  return apiClient.post<{ success: boolean; count: number; policies: NinjaPolicy[] }>('/ninja/policies/refresh', {})
}

export function getNinjaRoles() {
  return apiClient.get<NinjaRole[]>('/ninja/roles')
}

export function getCustomerOrgPolicies(customerId: string) {
  return apiClient.get<OrgPolicyMapping[]>(`/ninja/customers/${customerId}/org-policies`)
}

export function setCustomerOrgPolicies(customerId: string, mappings: OrgPolicyMapping[]) {
  return apiClient.put<{ success: boolean }>(`/ninja/customers/${customerId}/org-policies`, mappings)
}

// ─── Customer Locations ──────────────────────────────────────────────────────

export interface CustomerLocation {
  id: string
  tenantId: string
  customerId: string
  name: string
  address: string | null
  ninjaoneLocationId: string | null
  createdAt: string
  updatedAt: string
}

export interface LocationSyncResult {
  success: boolean
  action: 'create' | 'update'
  ninjaLocationId: number
  locationName: string
}

export function listCustomerLocations(customerId: string) {
  return apiClient.get<CustomerLocation[]>(`/ninja/customers/${customerId}/locations`)
}

export function createCustomerLocation(customerId: string, name: string, address?: string) {
  return apiClient.post<CustomerLocation>(`/ninja/customers/${customerId}/locations`, { name, address })
}

export function updateCustomerLocation(customerId: string, locationId: string, name: string, address?: string) {
  return apiClient.patch<CustomerLocation>(`/ninja/customers/${customerId}/locations/${locationId}`, { name, address })
}

export function deleteCustomerLocation(customerId: string, locationId: string) {
  return apiClient.delete<{ success: boolean }>(`/ninja/customers/${customerId}/locations/${locationId}`)
}

export function syncLocationToNinja(customerId: string, locationId: string) {
  return apiClient.post<LocationSyncResult>(`/ninja/customers/${customerId}/locations/${locationId}/sync`, {})
}

// ─── Scripts ─────────────────────────────────────────────────────────────────

export interface NinjaScript {
  id: number
  name: string
  language?: string | null
  description?: string | null
  type?: string | null
}

export interface NinjaDevice {
  id: number
  displayName: string
  systemName?: string | null
  online?: boolean | null
  lastContact?: number | null
  nodeRoleId?: number | null
  locationId?: number | null
}

export interface NinjaScriptRunResult {
  id: string
  ninjaRunId: string | null
  deviceId: number | null
  deviceName: string | null
  scriptId: number
  scriptName: string | null
  status: string
  isBulk: boolean
  errorMessage: string | null
  createdAt: string
}

export function listNinjaScripts() {
  return apiClient.get<NinjaScript[]>('/ninja/scripts')
}

export function refreshNinjaScriptsCache() {
  return apiClient.post<{ success: boolean; count: number; scripts: NinjaScript[] }>('/ninja/scripts/refresh', {})
}

export function getCustomerDevices(customerId: string) {
  return apiClient.get<NinjaDevice[]>(`/ninja/customers/${customerId}/devices`)
}

export interface NinjaOrgDevices {
  customerId: string
  customerName: string
  devices: NinjaDevice[]
  error?: string
}

export function getAllNinjaDevices() {
  return apiClient.get<NinjaOrgDevices[]>('/ninja/devices')
}

export function runNinjaScript(
  customerId: string,
  deviceId: number,
  deviceName: string,
  scriptId: number,
  scriptName: string,
  parameters?: string,
) {
  return apiClient.post<NinjaScriptRunResult & { success: boolean }>(`/ninja/customers/${customerId}/scripts/run`, {
    deviceId,
    deviceName,
    scriptId,
    scriptName,
    parameters,
  })
}

export function runNinjaScriptBulk(
  customerId: string,
  deviceIds: number[],
  scriptId: number,
  scriptName: string,
  parameters?: string,
) {
  return apiClient.post<NinjaScriptRunResult & { success: boolean }>(`/ninja/customers/${customerId}/scripts/run-bulk`, {
    deviceIds,
    scriptId,
    scriptName,
    parameters,
  })
}

export function listNinjaScriptRuns(customerId: string, limit = 20) {
  return apiClient.get<NinjaScriptRunResult[]>(`/ninja/customers/${customerId}/script-runs?limit=${limit}`)
}
